/**
 * OpenStack settings management page component for admins.
 */
import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useSettings } from '../contexts/SettingsContext';
import { updateSettings, SettingsUpdate } from '../api/settings';
import './OpenStackSettingsPage.css';

export const OpenStackSettingsPage: React.FC = () => {
  const { settings, refreshSettings } = useSettings();
  const [rejectPasswordAuth, setRejectPasswordAuth] = useState(true);
  const [allowedUrls, setAllowedUrls] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setRejectPasswordAuth(settings.openstack_reject_password_auth ?? true);
      setAllowedUrls(settings.openstack_allowed_urls || '');
    }
  }, [settings]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const updates: SettingsUpdate = {
        openstack_reject_password_auth: rejectPasswordAuth,
        openstack_allowed_urls: allowedUrls.trim() || null,
      };

      await updateSettings(updates);
      setSuccess('OpenStack settings updated successfully!');
      await refreshSettings();
    } catch (err: any) {
      let errorMessage = 'Failed to update OpenStack settings. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setRejectPasswordAuth(settings.openstack_reject_password_auth ?? true);
      setAllowedUrls(settings.openstack_allowed_urls || '');
      setError(null);
      setSuccess(null);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset OpenStack settings to defaults? This cannot be undone.')) {
      setRejectPasswordAuth(true);
      setAllowedUrls('');
      setError(null);
      setSuccess(null);
    }
  };

  if (!settings) {
    return (
      <AdminLayout>
        <div className="openstack-settings-page">
          <div className="openstack-settings-container">
            <div className="loading-message">Loading OpenStack settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="openstack-settings-page">
        <div className="openstack-settings-container">
          <div className="admin-breadcrumb">
            <Link to="/admin">Admin</Link>
            <span className="admin-breadcrumb-separator">/</span>
            <span className="admin-breadcrumb-current">OpenStack</span>
          </div>
          <h1>OpenStack Settings</h1>

          <form onSubmit={handleSubmit} className="openstack-settings-form" role="form">
            {error && (
              <div className="error-message" role="alert">
                <strong>Error:</strong> {error}
              </div>
            )}

            {success && (
              <div className="success-message" role="alert">
                {success}
              </div>
            )}

            <section className="settings-section">
              <div className="form-group">
                <label className="checkbox-label" htmlFor="reject-password-auth">
                  <input
                    id="reject-password-auth"
                    type="checkbox"
                    checked={rejectPasswordAuth}
                    onChange={(e) => {
                      setRejectPasswordAuth(e.target.checked);
                      setError(null);
                    }}
                    disabled={isLoading}
                  />
                  <span>Reject cloud configurations with username/password authentication</span>
                </label>
                <small className="form-hint">
                  When enabled, only application credential authentication will be allowed for cloud configurations.
                  Default: Not allowed (checked).
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="allowed-urls">Allowed OpenStack Auth URLs</label>
                <textarea
                  id="allowed-urls"
                  value={allowedUrls}
                  onChange={(e) => {
                    setAllowedUrls(e.target.value);
                    setError(null);
                  }}
                  disabled={isLoading}
                  placeholder="https://openstack.example.com:5000/v3, https://another-openstack.example.com:5000/v3"
                  rows={5}
                  className="url-list-input"
                />
                <small className="form-hint">
                  Comma-separated list of allowed OpenStack authentication URLs. Cloud configurations with URLs that do not start with any of these URLs will be rejected. Leave empty to allow all URLs.
                </small>
              </div>
            </section>

            <div className="form-actions">
              <button
                type="button"
                className="btn-reset"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Changes
              </button>
              <button
                type="button"
                className="btn-reset-defaults"
                onClick={handleResetToDefaults}
                disabled={isLoading}
              >
                Reset to Defaults
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

